import { mrrHistory } from '../../../mocks/superAdmin';
import { SATenant, SAStats } from '../../../hooks/useSuperAdmin';

const SA_ACCENT = '#6366F1';

const PLAN_PRICES: Record<string, number> = {
  Starter: 299,
  Professional: 899,
  Enterprise: 1999,
};

interface SAOverviewProps {
  tenants: SATenant[];
  stats: SAStats;
  loading: boolean;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function SAOverview({ tenants, stats, loading }: SAOverviewProps) {
  const planCounts = {
    Starter: tenants.filter((t) => t.plan === 'Starter' && t.status === 'Active').length,
    Professional: tenants.filter((t) => t.plan === 'Professional' && t.status === 'Active').length,
    Enterprise: tenants.filter((t) => t.plan === 'Enterprise' && t.status === 'Active').length,
  };
  const totalActive = Object.values(planCounts).reduce((a, b) => a + b, 0);

  const kpis = [
    {
      label: 'Total Agencies',
      value: loading ? '—' : stats.total_agencies,
      sub: loading ? '' : `${stats.trial_count} on trial`,
      icon: 'ri-building-4-line',
      color: SA_ACCENT,
    },
    {
      label: 'Active Paid',
      value: loading ? '—' : stats.active_paid_count,
      sub: 'Paying customers',
      icon: 'ri-checkbox-circle-line',
      color: '#10B981',
    },
    {
      label: 'Monthly MRR',
      value: loading ? '—' : `AED ${stats.total_mrr.toLocaleString()}`,
      sub: 'Live from DB',
      icon: 'ri-money-dollar-circle-line',
      color: '#F59E0B',
      isString: true,
    },
    {
      label: 'Platform Users',
      value: loading ? '—' : stats.total_users,
      sub: 'Across all agencies',
      icon: 'ri-group-line',
      color: '#EC4899',
    },
    {
      label: 'Trial Agencies',
      value: loading ? '—' : stats.trial_count,
      sub: 'Pending conversion',
      icon: 'ri-timer-line',
      color: '#F97316',
    },
  ];

  const recentSignups = [...tenants].slice(0, 5);

  const topAgencies = [...tenants]
    .filter((t) => t.status === 'Active')
    .sort((a, b) => b.bookings_mtd - a.bookings_mtd)
    .slice(0, 5);

  // Use mock history but replace last bar with real MRR
  const chartData = mrrHistory.map((d, i) =>
    i === mrrHistory.length - 1 ? { ...d, mrr: stats.total_mrr || d.mrr } : d
  );
  const maxMRR = Math.max(...chartData.map((d) => d.mrr), 1);

  const initials = (name: string) =>
    name
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading live data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-5 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ backgroundColor: `${k.color}18` }}
              >
                <i className={`${k.icon} text-base`} style={{ color: k.color }} />
              </div>
              <p className="text-xs text-slate-500 font-medium leading-tight">{k.label}</p>
            </div>
            <p className="text-2xl font-bold text-slate-900 mb-0.5">{k.value}</p>
            <p className="text-xs text-slate-400">{k.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* MRR Chart */}
        <div className="col-span-2 bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-slate-800 text-sm">Monthly Recurring Revenue</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Current month is live · historical is illustrative
              </p>
            </div>
            {stats.total_mrr > 0 && (
              <span className="text-xs px-2.5 py-1 rounded-full font-medium text-emerald-700 bg-emerald-100">
                AED {stats.total_mrr.toLocaleString()} live
              </span>
            )}
          </div>
          <div className="flex items-end gap-4 h-40">
            {chartData.map((d, i) => {
              const pct = (d.mrr / maxMRR) * 100;
              const isLast = i === chartData.length - 1;
              return (
                <div key={d.month} className="flex-1 flex flex-col items-center gap-2">
                  <p className="text-xs font-semibold text-slate-700">
                    AED {(d.mrr / 1000).toFixed(1)}K
                  </p>
                  <div
                    className="w-full rounded-t-lg transition-all"
                    style={{
                      height: `${pct}%`,
                      backgroundColor: isLast ? SA_ACCENT : `${SA_ACCENT}55`,
                    }}
                  />
                  <p className="text-xs text-slate-400">{d.month}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="font-bold text-slate-800 text-sm mb-1">Plan Distribution</h3>
          <p className="text-xs text-slate-400 mb-5">Active paying agencies</p>
          {totalActive === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <i className="ri-bar-chart-box-line text-3xl text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No paid agencies yet</p>
              <p className="text-xs text-slate-300">Trial agencies won't show here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {[
                { name: 'Enterprise', count: planCounts.Enterprise, color: '#6366F1', price: PLAN_PRICES.Enterprise },
                { name: 'Professional', count: planCounts.Professional, color: '#0D9488', price: PLAN_PRICES.Professional },
                { name: 'Starter', count: planCounts.Starter, color: '#16A34A', price: PLAN_PRICES.Starter },
              ].map((p) => {
                const pct = totalActive > 0 ? (p.count / totalActive) * 100 : 0;
                return (
                  <div key={p.name}>
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: p.color }}
                        />
                        <span className="font-medium text-slate-700">{p.name}</span>
                      </div>
                      <span className="text-slate-500 font-semibold">{p.count} agencies</span>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: p.color }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      AED {p.price}/mo · AED {(p.count * p.price).toLocaleString()} MRR
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Recent Signups */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800 text-sm">Recent Agency Signups</h3>
            <span className="text-xs text-slate-400">{tenants.length} total</span>
          </div>
          {recentSignups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <i className="ri-building-4-line text-3xl text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No agencies registered yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentSignups.map((t) => (
                <div key={t.id} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: t.brand_color || SA_ACCENT }}
                  >
                    {initials(t.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{t.name}</p>
                    <p className="text-xs text-slate-400">
                      {[t.city, t.country].filter(Boolean).join(', ') || 'Location not set'} ·{' '}
                      {formatDate(t.created_at)}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                      t.status === 'Trial'
                        ? 'bg-amber-100 text-amber-700'
                        : t.status === 'Active'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {t.status === 'Trial' ? '14-day trial' : t.plan}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Performing Agencies */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="font-bold text-slate-800 text-sm mb-4">Top Agencies by Bookings (MTD)</h3>
          {topAgencies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <i className="ri-calendar-check-line text-3xl text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No booking data yet this month</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topAgencies.map((t, i) => {
                const maxBookings = topAgencies[0].bookings_mtd || 1;
                const pct = (t.bookings_mtd / maxBookings) * 100;
                return (
                  <div key={t.id} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-slate-400 w-5 text-center">#{i + 1}</span>
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: t.brand_color || SA_ACCENT }}
                    >
                      {initials(t.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-slate-700 truncate">{t.name}</p>
                        <span className="text-xs font-bold text-slate-800 flex-shrink-0 ml-2">
                          {t.bookings_mtd}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: t.brand_color || SA_ACCENT }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
